<script>
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import StatusBadge from '../components/StatusBadge.svelte';

  let orders = [];
  let loading = true;
  let error = null;
  let view = 'current';
  let selected = new Set();

  onMount(load);

  async function load() {
    loading = true;
    error = null;
    selected = new Set();
    try {
      const data = await api(`/orders?view=${view}`);
      orders = data.orders;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  function toggle(num) {
    if (selected.has(num)) selected.delete(num);
    else selected.add(num);
    selected = selected;
  }

  function toggleAll(e) {
    if (e.target.checked) selected = new Set(orders.map((o) => o.orderNumber));
    else selected = new Set();
  }

  function printSelected() {
    if (!selected.size) return;
    window.location.hash = '#/labels/' + [...selected].join(',');
  }

  function fmtDate(s) {
    try {
      return new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    } catch (e) {
      return s;
    }
  }

  $: emptyMessage = view === 'current' ? 'Nothing to work on right now 🎉' : 'No recent completed orders.';
</script>

<div class="head">
  <div class="left">
    <h2>Orders {#if !loading && !error}<span class="count">{orders.length}</span>{/if}</h2>
    <select bind:value={view} on:change={load}>
      <option value="current">Current — to work on</option>
      <option value="recent">Recent — completed</option>
    </select>
  </div>
  <div class="right">
    {#if selected.size}
      <button class="primary" on:click={printSelected}>🏷️ Print {selected.size} label{selected.size === 1 ? '' : 's'}</button>
    {/if}
    <button on:click={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
  </div>
</div>

{#if loading}
  <p class="muted">Loading orders…</p>
{:else if error}
  <p class="error">⚠️ {error}</p>
{:else if orders.length === 0}
  <p class="muted">{emptyMessage}</p>
{:else}
  <table>
    <thead>
      <tr>
        <th class="cb"><input type="checkbox" on:change={toggleAll} checked={selected.size === orders.length && orders.length > 0} /></th>
        <th>Order</th><th>Date</th><th>Customer</th><th class="num">Items</th><th>Shipping</th><th>Status</th>
      </tr>
    </thead>
    <tbody>
      {#each orders as o (o.id)}
        <tr class:sel={selected.has(o.orderNumber)}>
          <td class="cb"><input type="checkbox" checked={selected.has(o.orderNumber)} on:change={() => toggle(o.orderNumber)} /></td>
          <td>
            <a href={`#/order/${o.orderNumber}`}>{o.name}</a>
            {#if o.isEcard}<span class="ecard" title={o.ecardCount > 1 ? `${o.ecardCount} ecards` : 'Contains an ecard'}>Ecard{#if o.ecardCount > 1} ×{o.ecardCount}{/if}</span>{/if}
          </td>
          <td>{fmtDate(o.createdAt)}</td>
          <td>{o.customerName}</td>
          <td class="num">{o.itemCount}</td>
          <td>{o.shippingMethod}</td>
          <td>
            <StatusBadge status={o.fulfillmentStatus} />
            {#if o.state && o.state.fulfillment === 'fulfilled'}<span class="tick" title="Fulfilled via dashboard">·✓</span>{/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
{/if}

<style>
  .head { display: flex; justify-content: space-between; align-items: center; gap: 1rem; }
  .left { display: flex; align-items: center; gap: 1rem; }
  .right { display: flex; align-items: center; gap: .6rem; }
  h2 { font-size: 1.25rem; margin: 0; }
  .count { display: inline-block; min-width: 1.4rem; text-align: center; font-size: .8rem; font-weight: 600; color: #555; background: #eee; border-radius: 999px; padding: .05rem .45rem; vertical-align: middle; }
  select { padding: .35rem .5rem; border: 1px solid #ccc; border-radius: 6px; font-size: .85rem; }
  button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .4rem .8rem; border-radius: 6px; }
  button.primary { background: #111; color: #fff; border-color: #111; }
  table { width: 100%; border-collapse: collapse; margin-top: 1rem; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.05); }
  th, td { text-align: left; padding: .6rem .85rem; border-bottom: 1px solid #f0f0f0; font-size: .9rem; }
  th { background: #f7f7f7; font-weight: 600; }
  td a { color: #1d4ed8; font-weight: 600; text-decoration: none; }
  .ecard { display: inline-block; margin-left: .4rem; background: #f3e8ff; color: #6b21a8; border-radius: 999px; padding: .05rem .45rem; font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; vertical-align: middle; }
  .cb { width: 1%; text-align: center; }
  .num { text-align: right; }
  tr.sel { background: #f0f7ff; }
  .tick { color: #166534; font-size: .8rem; }
  .muted { color: #666; }
  .error { color: #b00020; }
</style>
